/**
 * Loss Runs endpoint — a submission's historical claims/losses, backed by
 * the real `/v2/submissions/{id}/loss-runs/` API (jun-16).
 *
 *   GET    /v2/submissions/:id/loss-runs/?limit=300   → list + aggregates
 *   POST   /v2/submissions/:id/loss-runs/             body: LossRunWriteWire
 *   PATCH  /v2/submissions/:id/loss-runs/:rowId       body: LossRunWriteWire
 *   DELETE /v2/submissions/:id/loss-runs/:rowId
 *   GET    /v2/submissions/:id/loss-runs/summary      (see getLossRunSummary, part 2)
 *
 * The list response carries the card aggregates (computed over ALL rows)
 * and rows reference the schedule by `location_id`; the FE deals in
 * location names, so reads fetch the submission's locations in parallel
 * (deduped by the client against the page's own load) and writes take the
 * caller-provided locations list to map name → id.
 */
import type { Facility } from "../../types/facility";
import type { LossRunAggregates, LossRunListResponseWire, LossRunRow, LossRunSummary, LossRunSummaryWire, LossRunWire, LossRunWritePayload } from "../../types/loss-run";
import type { LossRunLocationRef } from "../adapters/loss-run";
import { lossRunToWriteWire, wireToAggregates, wireToLossRun, wireToSummary } from "../adapters/loss-run";
import { getJsonReal, sendJsonReal } from "../client";
import { listFacilities } from "./facilities";

/** Backend caps `limit` at 200. We want all rows in one view (no visible
 *  pagination; the table paginates FE-side at 300/page per Gustavo), so we
 *  walk every page. Aggregates are identical on every page (server computes
 *  them over the whole table) — keep the first. */
const PAGE_LIMIT = 200;

function lossRunsBase(submissionId: string): string {
    // Collection path keeps the trailing slash (FastAPI 307 otherwise).
    return `/v2/submissions/${encodeURIComponent(submissionId)}/loss-runs`;
}

function toLocationRefs(facilities: readonly Facility[]): LossRunLocationRef[] {
    return facilities.filter((f) => f.name.trim() !== "").map((f) => ({ id: f.id, name: f.name, state: f.state }));
}

export interface LossRunsList {
    rows: LossRunRow[];
    aggregates: LossRunAggregates;
    /** Schedule locations — the Location dropdown options + id⇄name map. */
    locations: LossRunLocationRef[];
}

async function fetchAllPages(submissionId: string): Promise<{ items: LossRunWire[]; first: LossRunListResponseWire }> {
    const items: LossRunWire[] = [];
    let first: LossRunListResponseWire | null = null;
    let offset = 0;
    for (;;) {
        const page = await getJsonReal<LossRunListResponseWire>(`${lossRunsBase(submissionId)}/?offset=${offset}&limit=${PAGE_LIMIT}`);
        first ??= page;
        items.push(...page.items);
        if (items.length >= page.total || page.items.length === 0) break;
        offset += PAGE_LIMIT;
    }
    return { items, first: first ?? ({ total: 0, items: [], total_paid: "", total_reserved: "", total_paid_and_reserved: "", loss_excess_sir: "", sir_adjusted_loss_ratio: "" } as LossRunListResponseWire) };
}

export async function listLossRuns(submissionId: string): Promise<LossRunsList> {
    const [{ items, first }, facilities] = await Promise.all([fetchAllPages(submissionId), listFacilities(submissionId)]);
    const locations = toLocationRefs(facilities);
    return {
        rows: items.map((w) => wireToLossRun(w, locations)),
        aggregates: wireToAggregates(first),
        locations,
    };
}

export function createLossRun(submissionId: string, payload: LossRunWritePayload, locations: readonly LossRunLocationRef[]): Promise<LossRunRow> {
    return sendJsonReal<LossRunWire>(`${lossRunsBase(submissionId)}/`, "POST", lossRunToWriteWire(payload, locations)).then((w) =>
        wireToLossRun(w, locations),
    );
}

export function patchLossRun(
    submissionId: string,
    rowId: string,
    payload: LossRunWritePayload,
    locations: readonly LossRunLocationRef[],
): Promise<LossRunRow> {
    return sendJsonReal<LossRunWire>(`${lossRunsBase(submissionId)}/${encodeURIComponent(rowId)}`, "PATCH", lossRunToWriteWire(payload, locations)).then(
        (w) => wireToLossRun(w, locations),
    );
}

export function deleteLossRun(submissionId: string, rowId: string): Promise<void> {
    return sendJsonReal<void>(`${lossRunsBase(submissionId)}/${encodeURIComponent(rowId)}`, "DELETE");
}

/** Loss Run Summary — the per-report-year breakdown that feeds the Annual
 *  Loss Summary and Loss Ratio Analysis tabs. The backend computes every
 *  value; the FE renders read-only. Refreshed after each Save. */
export async function getLossRunSummary(submissionId: string): Promise<LossRunSummary> {
    const wire = await getJsonReal<LossRunSummaryWire>(`${lossRunsBase(submissionId)}/summary`);
    return wireToSummary(wire);
}
